import React from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useElementsScoreSettings } from 'reducers/reviewCreationSlice';

import { Delete, Plus } from 'assets/assetsV1/iconComponents';

import { Button } from 'uiToolkitV1/Button';
import { Divider } from 'uiToolkitV1/Divider';
import { NumberInput } from 'uiToolkitV1/InputV2/NumberInput';
import { RadioSelect } from 'uiToolkitV1/RadioSelect';
import { TooltipInfoV1 } from 'uiToolkitV1/TooltipInfoV1';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

export const autoComputeRatingInitialSetting = [
  {
    upperLimit: 20,
    rating: 1,
  },
  {
    upperLimit: 40,
    rating: 2,
  },
  {
    upperLimit: 60,
    rating: 3,
  },
  {
    upperLimit: 80,
    rating: 4,
  },
  {
    upperLimit: 100,
    rating: 5,
  },
];

interface Setting {
  upperLimit: number;
  rating: number;
}

export const isScorePercentValidator = (scoreSetting: Setting[]) => {
  if (!scoreSetting) return false;
  return scoreSetting.every((setting, i) => {
    // previous element should not be greater than current
    if (i > 0 && scoreSetting[i - 1].upperLimit >= setting.upperLimit) {
      return false;
    }
    // last element should always be 100
    if (i === scoreSetting.length - 1) {
      return setting.upperLimit === 100;
    }
    return true;
  });
};

export const isScoreValidator = (scoreSetting: Setting[]) => {
  if (!scoreSetting) return false;
  return scoreSetting.every((setting, i) => {
    // previous element should not be greater than current
    if (i > 0 && scoreSetting[i - 1].rating >= setting.rating) {
      return false;
    }
    return true;
  });
};

const ScoreSettings = ({ scoreSetting = [], feedbackFormId, onChange }: ScoreSettingTableProps) => {
  // const [scoreSettings, setScoreSettings] = useState<Setting[]>(scoreSetting);
  const { isGoalRatingAutoComputed, autoComputeGoalRatingSettings } = useElementsScoreSettings({
    feedbackFormId,
  });

  const isPercentValid = isScorePercentValidator(scoreSetting);
  const isScoreValid = isScoreValidator(scoreSetting);

  const handleChange = (valueAsNumber, index: number, key: 'rating' | 'upperLimit') => {
    const newScoreSetting = scoreSetting.map((setting, i) => {
      if (i === index) {
        return { ...setting, [key]: valueAsNumber };
      }
      return setting;
    });
    onChange(newScoreSetting);
  };

  const handleDelete = (index: number) => {
    const newScoreSetting = scoreSetting.filter((_, i) => i !== index);
    onChange(newScoreSetting);
  };

  const handleAdd = () => {
    const newScoreSetting = [...scoreSetting, { upperLimit: 0, rating: 0 }];
    onChange(newScoreSetting);
  };

  // useEffect(() => {
  //   onChange(scoreSettings);
  // }, [onChange, scoreSettings]);

  return (
    <>
      <TextLabel mt={3} ml={2} color="text.g2">
        Note: Please try and match the rating scale to the one set in the template for easier interpretation. If
        different, the goal score will be recalculated to the question.
      </TextLabel>
      {isGoalRatingAutoComputed && autoComputeGoalRatingSettings.length === 0 && (
        <TextLabel color="alerts.negative" m="12px 8px 0">
          Please add the score range
        </TextLabel>
      )}
      {!isPercentValid && (
        <TextLabel color="alerts.negative" m="12px 8px 0">
          Please make sure score data totals to 100%
        </TextLabel>
      )}
      {isPercentValid && !isScoreValid && (
        <TextLabel color="alerts.negative" m="18px 8px 0">
          Please make sure score is in sequence
        </TextLabel>
      )}
      {scoreSetting.map((setting, i) => {
        const start = i - 1 >= 0 ? scoreSetting[i - 1].upperLimit : 0;

        return (
          <Flex key={i} borderBottom="1px solid" borderColor="neutral.EBEFF3" p="12px 6px 24px" alignItems="center">
            <Box cursor="pointer" tabIndex={0} onClick={() => handleDelete(i)}>
              <Delete color="text.g2" boxSize="24px" />
            </Box>
            <Box width="45px">
              <TextBodyMedium pl={3}>{Number.isNaN(start) ? '' : `${start}%`}</TextBodyMedium>
            </Box>
            <TextBodyMedium ml={4}>to</TextBodyMedium>
            <Box width="86px">
              <NumberInput
                ml={4}
                value={setting.upperLimit}
                onChange={(valueAsString, valueAsNumber) => handleChange(valueAsNumber, i, 'upperLimit')}
                min={0}
                max={10000}
                // inputMode="numeric"
                format={(val) => (Number.isNaN(val as number) ? '' : val)}
                isInvalid={!isPercentValid}
              />
            </Box>
            <TextBodyMedium>%</TextBodyMedium>
            <TextBodyMedium ml={4} mr={4}>
              is
            </TextBodyMedium>
            <Box width="86px">
              <NumberInput
                value={setting.rating}
                onChange={(valueAsString, valueAsNumber) => {
                  handleChange(valueAsNumber, i, 'rating');
                }}
                min={0}
                max={10000}
                inputMode="numeric"
                format={(val) => (Number.isNaN(val as number) ? '' : val)}
                isInvalid={isPercentValid && !isScoreValid}
              />
            </Box>
          </Flex>
        );
      })}
      <Button alignSelf="start" mt={3} variant="tertiary" StartIcon={() => <Plus boxSize="24px" />} onClick={handleAdd}>
        Add Score
      </Button>
    </>
  );
};

export interface ScoreSettingTableProps {
  scoreSetting: Setting[];
  onChange?: (settings: Setting[]) => void;
  isGoalSectionEditable?: boolean;
  setIsGoalSectionEditable?: (isGoalSectionEditable: boolean) => void;
  feedbackFormId: number;
}

export const ScoreSettingTable = ({
  scoreSetting,
  onChange,
  isGoalSectionEditable,
  setIsGoalSectionEditable,
  feedbackFormId,
}: ScoreSettingTableProps) => (
  <Flex
    bgColor="neutral.FDFDFD"
    p="12px"
    borderRadius="8px"
    border="1px solid"
    borderColor="neutral.EBEFF3"
    flexDirection="column"
  >
    {/* <Box p={'6px 8px'}>
        <Flex alignItems={'center'}>
          <TextBodyMedium mr={1}>Allow users to change rating: </TextBodyMedium>
          <TooltipInfoV1 title="Tooltip" />
        </Flex>
        <Flex mt={'14px'}>
          <Flex>
            <Radio isChecked={editScore} name="" onChange={() => setEditScore(true)}>
              <TextBodyMedium>Yes</TextBodyMedium>
            </Radio>
          </Flex>
          <Flex ml={'104px'}>
            <Radio isChecked={!editScore} name="" onChange={() => setEditScore(false)}>
              <TextBodyMedium>No</TextBodyMedium>
            </Radio>
          </Flex>
        </Flex>
      </Box> */}
    <Box p="12px 12px">
      <Flex alignItems="center">
        <TextBodyMedium mr={2} mb="2px">
          Allow reviewers to override the auto-calculated rating?
        </TextBodyMedium>
        <TooltipInfoV1 title="Show the auto-calculated rating but allow the reviewer to change the rating" />
      </Flex>

      <RadioSelect
        name="isGoalSectionEditableRadio"
        options={[{ name: 'Yes' }, { name: 'No' }]}
        selectedIndex={isGoalSectionEditable ? 0 : 1}
        onChange={(index) => setIsGoalSectionEditable(index === 0)}
      />
    </Box>

    <Divider mt={3} />
    <ScoreSettings scoreSetting={scoreSetting} onChange={onChange} feedbackFormId={feedbackFormId} />
  </Flex>
);
